import { useEffect, useState } from "react";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminFetch } from "./adminApi";

interface Dispute {
  DisputeID: number;
  BookingID: number;
  BookingCode?: string;
  RaisedBy: string;
  Reason: string;
  Description?: string;
  Status: string;
  ResolutionNote?: string;
  CreatedAt: string;
}

const STATUSES = ["open", "investigating", "resolved", "rejected"];

export default function AdminDisputes({ onBack }: { onBack: () => void }) {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
      const res = await adminFetch(`/api/admin/disputes${qs}`);
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message);
      setDisputes(payload.disputes || []);
    } catch (e) {
      toast.error("Failed to load disputes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    const bookingId = window.prompt("Booking ID to raise a dispute for:");
    if (!bookingId) return;
    const reason = window.prompt("Reason (short label):");
    if (!reason) return;
    const description = window.prompt("Description (optional):") || undefined;
    try {
      const res = await adminFetch(`/api/admin/disputes`, {
        method: "POST",
        body: JSON.stringify({ bookingId: Number(bookingId), reason, description }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message);
      toast.success("Dispute created.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to create dispute.");
    }
  };

  const updateStatus = async (dispute: Dispute, status: string) => {
    const resolutionNote = (status === "resolved" || status === "rejected")
      ? window.prompt("Resolution note:") || undefined
      : undefined;
    try {
      const res = await adminFetch(`/api/admin/disputes/${dispute.DisputeID}/status`, {
        method: "PUT",
        body: JSON.stringify({ status, resolutionNote }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message);
      toast.success("Dispute updated.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to update dispute.");
    }
  };

  return (
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-lg bg-[#0c1428] hover:bg-[#0f1e3d] text-[#a0aec0]"><ArrowLeft size={16} /></button>
          <h1 className="text-white text-lg sm:text-xl font-semibold">Disputes</h1>
        </div>
        <div className="flex items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-[#0a1020] border border-white/[0.08] text-white rounded-lg px-3 py-2 text-sm">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={handleCreate} className="px-3 py-2 bg-[#00c9a7]/10 hover:bg-[#00c9a7]/20 border border-[#00c9a7]/30 rounded-lg text-[#00c9a7] text-sm">Raise dispute</button>
          <button onClick={() => void load()} className="p-2 bg-[#0c1428] hover:bg-[#0f1e3d] border border-white/[0.07] rounded-lg text-[#a0aec0]"><RefreshCw size={16} /></button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-[#6b7fa3] text-sm py-14">Loading disputes…</div>
      ) : (
        <div className="space-y-3">
          {disputes.map((d) => (
            <div key={d.DisputeID} className="bg-[#0c1428] border border-white/[0.07] rounded-2xl p-4 text-sm text-[#e2e8f0]">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-medium text-white">Booking {d.BookingCode || d.BookingID} — {d.Reason}</div>
                  <div className="text-[#6b7fa3] text-xs mt-0.5">Raised by {d.RaisedBy} · {new Date(d.CreatedAt).toLocaleString()}</div>
                </div>
                <span className="px-2 py-1 bg-[#0a1020] rounded-lg text-xs">{d.Status}</span>
              </div>
              {d.Description && <p className="text-[#a0aec0] text-xs mb-3">{d.Description}</p>}
              {d.ResolutionNote && <p className="text-[#6b7fa3] text-xs mb-3">Resolution: {d.ResolutionNote}</p>}
              {(d.Status === "open" || d.Status === "investigating") && (
                <div className="flex gap-2">
                  {d.Status === "open" && <button onClick={() => updateStatus(d, "investigating")} className="px-3 py-1.5 bg-[#0a1020] hover:bg-[#0f1e3d] border border-white/[0.08] rounded-lg text-[#a0aec0] text-xs">Start investigating</button>}
                  <button onClick={() => updateStatus(d, "resolved")} className="px-3 py-1.5 bg-[#00c9a7]/10 hover:bg-[#00c9a7]/20 border border-[#00c9a7]/30 rounded-lg text-[#00c9a7] text-xs">Resolve</button>
                  <button onClick={() => updateStatus(d, "rejected")} className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-xs">Reject</button>
                </div>
              )}
            </div>
          ))}
          {disputes.length === 0 && <div className="text-center text-[#6b7fa3] text-sm py-14">No disputes found.</div>}
        </div>
      )}
    </div>
  );
}
